import {Locale} from '@dms/api/base';
import {UuidResponseBody} from '@dms/api/shared';

export type AddTemplateV3ApiResponse = /** status 201  */ UuidResponseBody;
export type AddTemplateV3ApiArg = {
  addTemplateRequestBody: AddTemplateV3RequestBody;
};
export type AddTemplateV3RequestBody = {
  documentCode: string;
  fileId: string | null;
  template: string | null;
  title: string;
  note: string | null;
  authorizationProfileId?: string | null;
  locale?: Locale | null;
};

export type CreateCustomFieldPropertyApiResponse = CreateCustomFieldPropertyResponseBody;

export type CreateCustomFieldPropertyResponseBody = {
  customFieldDefinitionId: string;
};

export type CreateCustomFieldPropertyApiArg = {
  customFieldDefinitionId: string;
  createCustomFieldPropertyRequestBody: CreateCustomFieldPropertyRequestBody;
};

export type CreateCustomFieldPropertyRequestBody = {
  propertyName: string;
};

export type RemoveCustomFieldPropertyApiResponse = /** status 204  */ void;
export type RemoveCustomFieldPropertyApiArg = {
  customFieldDefinitionId: string;
};
export type ListCustomFieldPropertyApiArg = void;
export type ListCustomFieldPropertyApiResponse = CustomFieldPropertyResponseBody[];
export type CustomFieldPropertyResponseBody = {
  customFieldDefinitionId: string;
  propertyName: string;
  documentPropertyName: string;
};
export type GetDefinitionTemplateListApiResponse =
  /** status 200  */ DefinitionTemplateListResponseBody;
export type GetDefinitionTemplateListApiArg = {
  withAuthorizationProfileId?: string;
  withoutAuthorizationProfile?: boolean;
  /** User JWT tokens are allowed */
  authorization?: string;
};
export type DocumentGroupEnum =
  | 'business_case'
  | 'vehicle'
  | 'cash_register_document'
  | 'accounting'
  | 'car_audit'
  | 'warehouse'
  | 'workshop'
  | 'pneu_hotel'
  | 'vehicle_warehouse'
  | 'car_rental';
export type UpdatedByResponseBody = {
  id: string;
  fullName: string;
};
export type TemplateResponseBody = {
  id: string;
  title: string;
  note: string | null;
  documentCode: string;
  type: 'system' | 'file' | 'string';
  updatedAt: string | null;
  updatedBy: UpdatedByResponseBody | null;
  authorizationProfileId: string | null;
  isHidden: boolean;
  canBeHidden: boolean;
  canBeDeleted: boolean;
  locale?: Locale | null;
};
export type DocumentAuthorizationProfileResponseBody = {
  authorizationProfileId: string;
  primaryTemplateId: string;
};
export type DefinitionWithTemplatesResponseBody = {
  id: string;
  documentCode: string;
  primaryTemplateId: string;
  title: string;
  group: DocumentGroupEnum;
  type: 'docx' | 'html';
  templates: TemplateResponseBody[];
  authorizationProfiles: DocumentAuthorizationProfileResponseBody[];
  resources: string[];
};
export type DefinitionTemplateListResponseBody = {
  definitions: DefinitionWithTemplatesResponseBody[];
};

export type GetDocumentGroupListApiResponse = DocumentGroupListResponseBody;
export type GetDocumentGroupListApiArg = void;

export type DocumentGroupResponseBody = {
  key: DocumentGroupEnum;
  title: string;
};
export type DocumentGroupListResponseBody = {
  groups: DocumentGroupResponseBody[];
};

export type ShowTemplateApiResponse = unknown;
export type ShowTemplateApiArg = {
  templateId: string;
};

export type HideTemplateApiResponse = unknown;
export type HideTemplateApiArg = {
  templateId: string;
};

export type SetPrimaryTemplateApiResponseV3 = unknown;
export type SetPrimaryTemplateApiArgV3 = {
  templateId: string;
};

export type DownloadTemplateApiResponse = Blob;
export type DownloadTemplateApiArg = {
  templateId: string;
};
export type DownloadTemplateApiResponseV3 = Blob;
export type DownloadTemplateApiArgV3 = {
  templateId: string;
};
export type DownloadTemplateSourceApiResponseV3 = string;
export type DownloadTemplateSourceApiArgV3 = DownloadTemplateApiArgV3;

export type SetPrimaryAuthProfileApiResponse = unknown;
export type SetPrimaryAuthProfileApiArg = {
  templateId: string;
  authorizationProfileId: string;
};
export type PatchTemplateApiRes = {
  templateId: string;
};
export type RemoveTemplateApiArg = {
  templateId: string;
};
export type RemoveTemplateApiResponse = unknown;
export type PatchTemplateApiResponse = unknown;
export type PatchTemplateApiArg = {
  templateId: string;
  patchTemplateRequestBody: PatchTemplateRequestBodyV3;
};
export type PatchTemplateRequestBodyV3 = {
  fileId?: string | null;
  template?: string | null;
  title?: string;
  note?: string | null;
  authorizationProfileId?: string | null;
  locale?: Locale | null;
};
export type DocumentDefinitionResponseBody = {
  id: string;
  documentCode: string;
  title: string;
  group: string;
  type: 'docx' | 'html';
  primaryTemplateId: string;
  authorizationProfiles: DocumentAuthorizationProfileResponseBody[];
};
export type DocumentDefinitionListResponseBody = {
  definitions: DocumentDefinitionResponseBody[];
};
export type GetDocumentDefinitionListApiResponse =
  /** status 200  */ DocumentDefinitionListResponseBody;
export type GetDocumentDefinitionListApiArg = {
  group?: DocumentGroupEnum;
};
export type GetTemplatePreviewExampleApiResponse =
  /** status 200  */ TemplatePreviewExampleResponseBody;
export type GetTemplatePreviewExampleApiArg = {
  documentCode: string;
  /** Tenant-enabled locale to resolve translations/template variant for. Omit to use the tenant default. */
  locale?: Locale;
};
/**
 * Normalized, JSON-safe render context for the front-end twig.js document preview. Top-level keys
 * mirror the PHP Twig export context: `parameters` (the document DTO) plus `template.logoUrl`
 * (always `null` in preview). `Money` normalizes to `{amount: string, currency: string}`, `Date` to
 * `Y-m-d`, `DateTimeImmutable` to RFC 3339, and backed enums to their scalar value.
 */
export type TemplatePreviewExampleContext = {
  parameters: Record<string, unknown>;
  template: {
    logoUrl: string | null;
  };
};
export type TemplatePreviewExampleResponseBody = {
  documentCode: string;
  locale: Locale;
  resolvedTemplateLocale: Locale;
  availableLocales: Locale[];
  context: TemplatePreviewExampleContext;
};
